import type { AuthFailureKind } from '../auth/failure.ts';
import { redactUrl } from '../instance/urls.ts';

/**
 * R6 — failure policy lives in core, written once and called from both `shaka.util.Error` and
 * Media3's `onPlayerError`.
 *
 * R6 names four kinds (`urls-expired`, `instance-warming`, `video-unavailable`, `network`). The
 * rest exist because §3's retry policy cannot be expressed without them: "never retry 401/403,
 * do retry 429/5xx" needs `unauthorized` and `rate-limited` to be distinct from `client` and
 * `server`. `aborted` and `malformed-response` are the non-HTTP failure modes the transport can
 * produce.
 */
export type ApiErrorKind =
  | 'urls-expired'
  | 'instance-warming'
  | 'video-unavailable'
  | 'unauthorized'
  | 'rate-limited'
  | 'server'
  | 'client'
  | 'network'
  | 'aborted'
  | 'malformed-response'
  /**
   * Invidious *itself* throwing on this particular input — a leaked Crystal exception, not a sick
   * instance. See {@link isLeakedServerException} for why it is its own kind.
   */
  | 'upstream-broken'
  /**
   * The instance's administrator switched this endpoint off. Permanent for as long as the config
   * says so, and about *one* endpoint rather than about the instance — see
   * {@link isEndpointDisabled}.
   */
  | 'endpoint-disabled';

/**
 * Every kind, as data — so something other than a `switch` can walk the taxonomy.
 *
 * The `Record` is what makes it complete rather than a list somebody has to remember to update: adding
 * an arm to {@link ApiErrorKind} without adding it here is a compile error. That is the same trick the
 * web app's copy table plays one layer up, and between the two of them a new failure mode cannot reach
 * a reader as its own discriminant — which is exactly how `urls-expired`, `server`, `client` and
 * `malformed-response` used to arrive on screen.
 */
const EVERY_KIND: Readonly<Record<ApiErrorKind, true>> = {
  'urls-expired': true,
  'instance-warming': true,
  'video-unavailable': true,
  unauthorized: true,
  'rate-limited': true,
  server: true,
  client: true,
  network: true,
  aborted: true,
  'malformed-response': true,
  'upstream-broken': true,
  'endpoint-disabled': true,
};

export const API_ERROR_KINDS: readonly ApiErrorKind[] = Object.keys(EVERY_KIND) as ApiErrorKind[];

export interface ApiError {
  readonly kind: ApiErrorKind;
  /** `null` for failures that never reached a response. */
  readonly status: number | null;
  /** Always redacted (R9) — this object ends up in logs and error UIs. */
  readonly url: string;
  readonly retryable: boolean;
  /**
   * Only set on `unauthorized`, and only by the authenticated classifier: the instance overloads
   * one `403` across five unrelated auth failures, and which one it was decides whether the app
   * signs the user in, silently re-authorizes, or asks for wider scopes (§8).
   */
  readonly auth?: AuthFailureKind;
  /**
   * Only set on `video-unavailable`, and only when the body says which thing was missing — the same
   * shape as {@link ApiError.auth} and for the same reason: the status is one value over several
   * meanings and the discriminator exists only inside the transport. See {@link notFoundSubject}.
   */
  readonly notFound?: NotFoundSubject;
  /**
   * How long until a scheduled live stream begins, in seconds. Only set alongside
   * `notFound: 'video-not-yet-live'`, and only when the relayed sentence carried a number —
   * "in a few moments" carries none. A number rather than the sentence, because the words a reader
   * sees are the app's to choose (R1's other half: core hands over quantities).
   */
  readonly startsInSec?: number;
}

/**
 * **`404` on the playlist endpoints means three different things and only the body separates them**
 * (measured across the five mutations):
 *
 * ```
 * PATCH  /api/v1/auth/playlists/IVPLzzz…            → 404 {"error":"Playlist does not exist."}
 * DELETE /api/v1/auth/playlists/IVPL…/videos/000…   → 404 {"error":"Playlist does not contain index"}
 * POST   /api/v1/auth/playlists/IVPL…/videos        → 404 {"error":"Video unavailable"}
 * ```
 *
 * All three are `video-unavailable` by status, which R6 named for a video and 2e already stretched to
 * cover a dead playlist link. That was fine while every 404 on this family meant one thing; with three
 * it is not, because the three lead to different sentences *and* different recoveries — refresh the
 * playlist list, refresh this playlist's entries, or tell the reader that video cannot be added.
 *
 * Modelled as a field on the existing kind rather than as three new kinds, deliberately: the *policy*
 * is identical (never retried), so a new `ApiErrorKind` would be a
 * taxonomy change with no behaviour behind it. What the field adds is the noun, which is a copy
 * decision, and `undefined` — a 404 whose body says nothing recognisable — leaves every existing screen
 * on the sentence it already had.
 *
 * The middle one is the sharpest: *"Playlist does not contain index"* is what a **stale or wrongly-keyed
 * `indexId`** answers, and it reads as "that position no longer exists" even when the position plainly
 * does. Both readings mean "re-read the playlist", which is what makes one arm enough for it.
 *
 * `video-removed`, `video-private`, `video-age-restricted` and `video-members-only` widen this from
 * "which thing was missing" to "which thing, and what became of it" — see {@link videoGoneSubject},
 * where the body says so in YouTube's own words. Still the same kind and the same policy, for the
 * reason above: what they add is the noun.
 *
 * `video-not-yet-live` widens it once more, to "not there **yet**" — the same kind and the same
 * policy again, about a video with nothing wrong with it. It is here rather than in a kind of its own
 * for the reason the four above are: what a scheduled stream and a removed video share is that asking
 * a second time changes nothing, and that is the whole of what a kind decides.
 */
export type NotFoundSubject =
  | 'playlist'
  | 'playlist-entry'
  | 'video'
  | 'channel'
  | 'video-removed'
  | 'video-private'
  | 'video-age-restricted'
  | 'video-members-only'
  /** A live stream that is scheduled and has not begun — see {@link isLiveStreamNotStarted}. */
  | 'video-not-yet-live';

const NOT_FOUND_PLAYLIST_RE = /playlist does not exist/i;
const NOT_FOUND_ENTRY_RE = /playlist does not contain index/i;
const NOT_FOUND_VIDEO_RE = /video unavailable/i;

export function notFoundSubject(body: string): NotFoundSubject | null {
  // The entry check runs first: both messages start with "Playlist does not", and the broader one
  // would claim this body if it were asked first.
  if (NOT_FOUND_ENTRY_RE.test(body)) return 'playlist-entry';
  if (NOT_FOUND_PLAYLIST_RE.test(body)) return 'playlist';
  if (NOT_FOUND_VIDEO_RE.test(body)) return 'video';
  return null;
}

/**
 * A channel that is *gone* — measured 2026-08-01 against `/api/v1/channels/{ucid}`:
 *
 * ```
 * 500 {"error":"This channel was removed because it violated our Community Guidelines."}
 * 404 {"error":"This channel does not exist."}
 * ```
 *
 * The first is YouTube's own termination notice relayed verbatim (the wording varies — "This account
 * has been terminated for violating Google's Terms of Service." is another face of it), carried on a
 * **500** with no `errorBacktrace` and no relayed-status marker, so nothing above claims it and it
 * used to fall through to `server`: retryable, and retried three times. A subscription list holding a
 * few terminated channels then spent three requests on each one every time the cache moved, and the
 * whole page reported the instance as unwell about an instance that was perfectly healthy.
 *
 * Deliberately **not** matched: `{"error":"Could not get channel info."}` (transient — the same
 * channel answered 200 on twelve consecutive retries, §7c) and the subscribe endpoint's
 * `{"error":"Deleted or invalid channel"}` (a *write* refusal that already classifies as `client`).
 */
const CHANNEL_GONE_RE = /channel was removed|account has been terminated|channel does not exist/i;

export function isChannelGone(status: number, body: string): boolean {
  return status >= 400 && CHANNEL_GONE_RE.test(body);
}

/**
 * A video **YouTube itself will not serve**, in the words YouTube chose — the same hazard
 * {@link isChannelGone} covers, one endpoint over, and reached far more often.
 *
 * Invidious raises `InfoException(reason)` for any `playabilityStatus` refusal except the literal
 * `Video unavailable` (which it raises as a not-found and answers `404`), and `error_json_helper`
 * serialises an `InfoException` **without** `errorBacktrace`. So the whole family arrives as a bare
 * `500 {"error":"<YouTube's own sentence>"}` that nothing above claims, and it used to fall through to
 * `server`: retried three times. A reader walking a playlist full of dead entries would spend three
 * requests on every one of them and be told the instance was unwell, about an instance that was well.
 *
 * Nine bodies — seven measured 2026-08-06 by probing the blanked entries of six playlists, the
 * eighth measured 2026-08-10 on `2xRMit_8oCs` (the same body on 5 of 5 consecutive requests), and the
 * ninth measured 2026-08-24 on `iRw0EPtFiP8` (the same body on 3 of 3). Every one a `500` with no
 * `errorBacktrace`, and every one about the video rather than about the instance:
 *
 * ```
 * This video has been removed by the uploader
 * This video has been removed for violating YouTube's Terms of Service
 * This video has been removed for violating YouTube's Community Guidelines
 * This video has been removed for violating YouTube's policy on hate speech. …
 * This video is no longer available because the YouTube account … has been terminated.
 * It was removed following a copyright removal request by <claimant>
 * Sign in if you've been granted access to this video          ← a private video
 * This video may be inappropriate for some users.              ← an age restriction
 * This video is available to this channel's members on level: <level> …    ← members-only
 * ```
 *
 * Four nouns rather than one, because they lead to four different sentences: six of the nine mean
 * the video is *gone*, the seventh means it exists and is not shared, the eighth means it exists
 * and the instance has no signed-in YouTube account to fetch it with, and the ninth means it exists
 * and YouTube shows it only to the channel's paying members.
 *
 * **`Sign in to confirm your age` belongs to the age arm as well**, and that reverses an earlier call.
 * It is YouTube's own wording for this same refusal, and it used to be left to `server` because the
 * only two arms on offer were "private" and "retry" — of which "private" was plainly wrong and "retry"
 * was merely wrong quietly: the age restriction on a video does not lift between two requests, so the
 * three attempts bought nothing. With an arm of its own there is nothing left to weigh. The private matcher is still keyed on `granted access to this
 * video` rather than on its opening words, and that is now what keeps the two apart in the *other*
 * direction: it declines the age body, so the age arm gets it.
 *
 * The four matchers are disjoint on all nine bodies, so the first-match ordering below is a
 * statement about cost rather than about meaning.
 *
 * **Deliberately not matched**: `{"error":"We're processing this video. Check back later."}` (measured
 * on `5qap5aO4i9A`). It is the same shape from the same code path and it is *transient* — the video
 * arrives when YouTube finishes — so it keeps §3's retry path, which is the call
 * `{"error":"Could not get channel info."}` already established one endpoint over.
 *
 * Regex rather than `JSON.parse` for the reason `auth/failure.ts` gives, and unreachable for media,
 * which passes no body.
 */
const VIDEO_REMOVED_RE =
  /video has been removed|removed following a copyright removal request|account associated with this video has been terminated/i;

const VIDEO_PRIVATE_RE = /granted access to this video/i;

/**
 * One expression over both wordings, because they are one condition. Invidious relays whichever
 * sentence the `playabilityStatus` carried, and a reader needs the same sentence from us either way.
 *
 * Neither alternative can reach a body the two matchers above want: `may be inappropriate for some
 * users` shares no phrase with any of them, and `confirm your age` is exactly the string
 * {@link VIDEO_PRIVATE_RE} was narrowed to avoid.
 */
const VIDEO_AGE_RESTRICTED_RE = /may be inappropriate for some users|confirm your age/i;

/**
 * Two alternatives for one condition, because YouTube words this refusal with and without the
 * membership level: "This video is available to this channel's members on level: <level> …" and
 * "Join this channel to get access to members-only content …". The level's name is interpolated
 * into the first wording — the copyright claimant's situation one matcher up — so only the fixed
 * phrases are matchable.
 *
 * Neither alternative shares a phrase with the three matchers above, so none of them can claim
 * these bodies.
 */
const VIDEO_MEMBERS_ONLY_RE = /this channel's members|members-only content/i;

export function videoGoneSubject(status: number, body: string): NotFoundSubject | null {
  if (status < 400) return null;
  if (VIDEO_REMOVED_RE.test(body)) return 'video-removed';
  if (VIDEO_PRIVATE_RE.test(body)) return 'video-private';
  if (VIDEO_AGE_RESTRICTED_RE.test(body)) return 'video-age-restricted';
  if (VIDEO_MEMBERS_ONLY_RE.test(body)) return 'video-members-only';
  return null;
}

/**
 * **A live stream that is scheduled and has not begun** — the same relayed-refusal shape as
 * {@link videoGoneSubject}, about a video that is not in trouble at all.
 *
 * Invidious raises YouTube's `LIVE_STREAM_OFFLINE` reason through the same `InfoException` path, so
 * it arrives as a bare `500` with no `errorBacktrace` and nothing above claims it. It used to fall
 * through to `server`: retried three times and reported to the reader
 * as "The instance failed on this. The instance is unwell" — about an instance that was well and a
 * stream that was simply not on yet.
 *
 * Four wordings, measured 2026-08-25 by probing 539 zero-length videos off 508 channels. **51 of the
 * 52 failures in that sample were this one family**, which is what makes it worth its own arm rather
 * than a footnote:
 *
 * ```
 * This live event will begin in 70 minutes.
 * This live event will begin in 44 hours.
 * This live event will begin in 3 days.
 * This live event will begin in a few moments.
 * ```
 *
 * The policy is the one every arm of {@link videoGoneSubject} has — never retried — and for the
 * sharpest version of its reason: the start time does not move between two requests, so the three
 * attempts bought nothing.
 *
 * Matched on the fixed half of the sentence, since the tail is where the number and the unit live.
 * It shares no phrase with the four matchers above, so none of them can claim these bodies and this
 * one claims none of theirs. **`This live event has ended.` is deliberately not matched**: a stream
 * that is over is a different fact with a different answer, and the endpoint usually has the
 * recording by then.
 */
const LIVE_NOT_STARTED_RE = /live event will begin/i;

export function isLiveStreamNotStarted(status: number, body: string): boolean {
  return status >= 400 && LIVE_NOT_STARTED_RE.test(body);
}

/**
 * The wait out of that sentence, in seconds, or `null` when it carries no number.
 *
 * Minutes, hours and days are the three units measured, and YouTube does not normalise them — "44
 * hours" rather than "2 days" is what it sent. A unit this does not know, and the numberless "in a
 * few moments", both answer `null`, which the app reads as "say that it has not started and leave
 * the timing out" rather than as an error.
 */
const LIVE_STARTS_IN_RE = /live event will begin in (\d+) (minute|hour|day)s?/i;

const UNIT_SECONDS: Readonly<Record<string, number>> = { minute: 60, hour: 3600, day: 86_400 };

export function liveStreamStartsInSeconds(body: string): number | null {
  const match = LIVE_STARTS_IN_RE.exec(body);
  if (match === null) return null;
  const amount = Number(match[1]);
  const unit = UNIT_SECONDS[(match[2] ?? '').toLowerCase()];
  if (unit === undefined || !Number.isFinite(amount)) return null;
  return amount * unit;
}

function apiError(
  kind: ApiErrorKind,
  status: number | null,
  url: string,
  retryable: boolean,
  auth?: AuthFailureKind,
): ApiError {
  const base = { kind, status, url: redactUrl(url), retryable };
  return auth === undefined ? base : { ...base, auth };
}

/** The authenticated arm of {@link classifyHttpError} — see `auth/failure.ts` for why it exists. */
export function unauthorizedError(status: number, url: string, auth: AuthFailureKind): ApiError {
  return apiError('unauthorized', status, url, false, auth);
}

/**
 * "This request was wrong", built directly rather than inferred from a status. Exported for
 * `classifyAuthenticatedHttpError`, which has one reading `classifyHttpError` cannot make on its own:
 * a `403` on an `/api/v1/auth/*` path is never an expired media URL, whatever its body says.
 */
export function clientError(status: number, url: string): ApiError {
  return apiError('client', status, url, false);
}

/**
 * Invidious leaking one of its own exceptions, rather than the instance being unwell.
 *
 * ```
 * GET /api/v1/comments/1recdRrkg_Y            →  500
 * {"error":"Missing hash key: \"commentRenderer\"","errorBacktrace":"… (KeyError)\n from ???"}
 *
 * GET /api/v1/search?q=lofi&type=video&page=35 →  400
 * {"error":"Missing hash key: \"primaryContents\"","errorBacktrace":"… (KeyError)\n from ???"}
 * ```
 *
 * The distinction decides behaviour, not taxonomy. §3's policy retries a 5xx with backoff — correct
 * for an instance that is restarting, and exactly wrong here: this failure is a *deterministic
 * function of the input*. Measured on 2026-07-28, the same video answered 500 on four consecutive
 * requests and on every later one, so three retries buy nothing and a page full of such videos
 * spends four requests on each of them. It is common enough for that to be a live hazard rather than
 * a hypothetical: 13 of 45 videos sampled off `/popular` answer this way for `sort_by=top`
 * (API reference §6).
 *
 * **The status floor is gone, and this is why.** This check used to require `status >= 500`,
 * because every observed case was a 500. Search leaks the identical shape as a **400** — on deep
 * paging and on `type=show` — and a 400 fell through to `client`, so the app said "the instance
 * returned an error (HTTP 400)" about a response Invidious had failed to *build*. Worse, the honest
 * reading of that particular 400 is often "you have reached the last page", which nothing downstream
 * could recover from a `client` error (`search/paging.ts`).
 *
 * It is matched **first** now, above the status arms, for the reason the floor existed in the first
 * place: whatever else a status means, a body carrying Invidious's own backtrace means Invidious
 * threw, and no per-status reading can be more specific than that. In particular a 403 with a
 * backtrace is not an expired media URL — and it cannot collide with media classification at all,
 * because media passes no body (see {@link classifyHttpError}).
 *
 * `errorBacktrace` is the discriminator because it is Invidious's own error shape and nothing
 * else's. A gateway 502, a restart's 503, an overload's 500, an expired media URL's 400 — none of
 * them carry it, so they keep today's behaviour, which is what they deserve. Matched with a regex
 * rather than `JSON.parse` for the same reason `auth/failure.ts` does: R4 keeps JSON text out of
 * core, and a body that is not JSON at all must not throw here.
 */
const LEAKED_EXCEPTION_RE = /"errorBacktrace"\s*:/;

export function isLeakedServerException(status: number, body: string): boolean {
  return status >= 400 && LEAKED_EXCEPTION_RE.test(body);
}

/**
 * Invidious relaying **YouTube's own** refusal. Same consequence as {@link isLeakedServerException}
 * and a different cause, which is why the two are separate matchers with one outcome.
 *
 * ```
 * GET /api/v1/channels/UCbYM7oBcZNzqQp98PiF5HKw          →  500      ← a "NEWS - Topic" channel
 * {"error":"Error: non 200 status code. Youtube API returned status code 500. See <a href=…"}
 *
 * GET /api/v1/channels/UC…/videos?continuation=garbage    →  500      ← a spent or mangled token
 * {"error":"Error: non 200 status code. Youtube API returned status code 400. See <a href=…"}
 * ```
 *
 * **It carries no `errorBacktrace`**, so the check above cannot see it, and it is a `500` — which §3
 * retries three times with backoff. That is the 1e hazard in a second place, and this measured how live it is: of **60 channels reachable from five
 * plain searches, 58 answer 200 and 2 fail**, one of them this way. Retrying buys nothing —
 * **10 of 10 consecutive requests** for the Topic channel returned it, and a spent continuation stays
 * spent — so it is `upstream-broken`: never retried, for exactly the reason the leaked-exception arm
 * is.
 *
 * **`{"error":"Could not get channel info."}` is deliberately *not* matched here, and that is the
 * measurement that changed this function.** It is the other of the two failures in that sample of 60
 * (Fox News), it is also a bare `500` with no backtrace, and it looks like an obvious member of this
 * family — but it is **transient**: the same channel answered `200` on the next twelve consecutive
 * requests. So it keeps §3's retry path, which is the whole point of having one. The same
 * body *also* comes back for `/api/v1/channels/@LofiGirl`, where no retry can ever help, so the string
 * genuinely means two different things and only one of them is permanent — and when a discriminator is
 * ambiguous, the arm that costs three requests beats the arm that refuses to recover.
 *
 * **What it relays is not always a breakage, and here is the case where that matters.** A
 * playlist that does not exist answers `404` with this exact body, relaying YouTube's own `404`:
 *
 * ```
 * GET /api/v1/playlists/PLzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzz    →  404
 * {"error":"Error: non 200 status code. Youtube API returned status code 404. See <a href=…"}
 * ```
 *
 * Matched as written above, that is `upstream-broken` — "Invidious could not build this response" —
 * about a link that is simply dead, and it outranks the `404` arm because this check is deliberately
 * first. The relayed status is in the body, so the honest reading needs no new evidence, only reading
 * the number that is already there: see {@link relayedUpstreamStatus}. A relayed `404` is
 * `video-unavailable` (the same three words a mistyped video id gets), every other relayed status
 * stays `upstream-broken`, and the two 2d measurements are untouched because they relay `500` and
 * `400`.
 *
 * Regex rather than `JSON.parse` for the reason `auth/failure.ts` gives, and unreachable for media
 * because media classification passes no body.
 */
const RELAYED_UPSTREAM_RE = /Youtube API returned status code/i;

export function isRelayedUpstreamFailure(status: number, body: string): boolean {
  return status >= 400 && RELAYED_UPSTREAM_RE.test(body);
}

/**
 * The status **YouTube** returned, out of the body Invidious relayed it in — or `null` when this is
 * not that kind of failure, or when the number is missing.
 *
 * It exists because "the upstream refused" and "the upstream said it is not there" want different
 * sentences, and the difference is one integer that the message already carries. `WL` and `LL` (Watch
 * Later and Liked videos, which need an account) relay a `404` too, so this arm is reached by more
 * than a typo.
 */
const RELAYED_UPSTREAM_STATUS_RE = /Youtube API returned status code (\d{3})/i;

export function relayedUpstreamStatus(body: string): number | null {
  const match = RELAYED_UPSTREAM_STATUS_RE.exec(body);
  if (match === null) return null;
  const status = Number(match[1]);
  return Number.isFinite(status) ? status : null;
}

/**
 * An endpoint the instance's administrator turned off — §8's "grey out Popular **with a reason** if
 * the instance disabled it", which is not implementable without being able to tell that case apart.
 *
 * It has to be read from the body, because the status is `403`:
 *
 * ```crystal
 * // src/invidious/routes/api/v1/feeds.cr — Invidious master, read 2026-07-29
 * if !CONFIG.popular_enabled
 *   error_message = {"error" => "Administrator has disabled this endpoint."}.to_json
 *   haltf env, 403, error_message
 * end
 * ```
 *
 * So **`403` is now overloaded across three subsystems**, not the two CLAUDE.md records: on media it
 * means "reload the manifest", on `/api/v1/auth/*` it means "signed out", and here it means "this
 * feature does not exist on this instance". Without this arm, an instance with `popular_enabled:
 * false` renders Home as `urls-expired` — the app announcing that its media URLs expired about a
 * request that fetched no media.
 *
 * The second pattern is a real deployment rather than a hypothetical: `inv.nadeko.net` answers
 * `403 Endpoint disabled` as **plain text** for `/api/v1/trending`, from in front of Invidious. Same
 * meaning, different layer, and a reader does not care which. Both are matched with a regex rather
 * than `JSON.parse` for the reason `auth/failure.ts` gives — R4 keeps JSON text out of core, and one
 * of these two bodies is not JSON at all.
 *
 * Restricted to 403 on purpose. A blanket "any status whose body says disabled" would let a
 * *media* 403 — whose body is googlevideo's, not the instance's — escape the expiry-recovery path
 * that §7 depends on; media classification passes no body, so with the status pinned it cannot.
 */
const ENDPOINT_DISABLED_RE = /disabled this endpoint|endpoint disabled/i;

export function isEndpointDisabled(status: number, body: string): boolean {
  return status === 403 && ENDPOINT_DISABLED_RE.test(body);
}

/**
 * Call this for a non-2xx status. A success status is not a failure and is reported as a
 * non-retryable `client` error so the function stays total — check {@link isSuccessStatus} first.
 *
 * 403/410 is the one that matters most: signed media URLs expire after ~5h45m (§2), so this is
 * the reload-the-manifest-and-resume path, not an error to show the user.
 *
 * `body` is optional: media failures are classified from a status alone, and passing `''` simply
 * means "no leaked-exception evidence", which is the honest reading of not having looked.
 */
export function classifyHttpError(status: number, url: string, body = ''): ApiError {
  // First, and above every status arm: a body carrying Invidious's own backtrace says Invidious
  // threw, whatever status it chose to throw it at. Unreachable for media, which passes no body.
  if (isLeakedServerException(status, body)) return apiError('upstream-broken', status, url, false);
  // Then the same outcome for a refusal Invidious is only *relaying* from YouTube — a Topic channel,
  // a spent continuation. Also a 500, also deterministic (10 of 10), also pointless to retry, and it
  // carries no backtrace so the check above cannot see it.
  //
  // Split on *what* was relayed, because a relayed 404 is a dead link rather than a breakage: a
  // nonexistent playlist answers `404` with this body, and "Invidious could not build this response"
  // is the wrong sentence for it. Both arms are non-retryable, so this changes the words and nothing
  // else.
  if (isRelayedUpstreamFailure(status, body)) {
    const relayed = relayedUpstreamStatus(body);
    return relayed === 404
      ? apiError('video-unavailable', status, url, false)
      : apiError('upstream-broken', status, url, false);
  }
  // Then the two body-aware 403 readings, before the status arm that would claim them: an endpoint
  // the administrator switched off is not an expired URL, and 403 is the status both wear.
  if (isEndpointDisabled(status, body)) return apiError('endpoint-disabled', status, url, false);
  // A deleted or terminated channel, whatever status it arrived on — the 500-shaped termination
  // notice must be read *before* the 5xx arm turns it into a retryable `server`.
  // One reading for all its faces: not found, subject `channel`, never retried.
  if (isChannelGone(status, body)) {
    return { ...apiError('video-unavailable', status, url, false), notFound: 'channel' };
  }
  // The same reading for a video YouTube took down or never shared, and in the same place for the same
  // reason: it wears a 500, so the arm below would make it retryable.
  const gone = videoGoneSubject(status, body);
  if (gone !== null) {
    return { ...apiError('video-unavailable', status, url, false), notFound: gone };
  }
  // And the same again for a stream that has not started. In the same place for the same reason —
  // it wears a 500 — and it is the most common member of the whole family: 51 of 52 failures in the
  // sweep that found it. The wait travels with it where the sentence carried one.
  if (isLiveStreamNotStarted(status, body)) {
    const scheduled = {
      ...apiError('video-unavailable', status, url, false),
      notFound: 'video-not-yet-live' as const,
    };
    const startsInSec = liveStreamStartsInSeconds(body);
    return startsInSec === null ? scheduled : { ...scheduled, startsInSec };
  }
  if (status === 403 || status === 410) return apiError('urls-expired', status, url, false);
  if (status === 401) return apiError('unauthorized', status, url, false);
  if (status === 404) {
    // The kind and the policy are unchanged; the body only says *which* thing was missing, and only
    // when it says so recognisably. Media passes no body, so this is unreachable for a media 404.
    const subject = notFoundSubject(body);
    const base = apiError('video-unavailable', status, url, false);
    return subject === null ? base : { ...base, notFound: subject };
  }
  if (status === 429) return apiError('rate-limited', status, url, true);
  if (status === 503) return apiError('instance-warming', status, url, true);
  if (status >= 500) return apiError('server', status, url, true);
  return apiError('client', status, url, false);
}

/** A CORS-blocked fetch is indistinguishable from a dead network in JS (§3) — both land here. */
export function networkError(url: string): ApiError {
  return apiError('network', null, url, true);
}

export function abortedError(url: string): ApiError {
  return apiError('aborted', null, url, false);
}

/** A 200 whose body is not what the endpoint promised — including the zero-byte caption response. */
export function malformedResponseError(url: string, status: number | null = 200): ApiError {
  return apiError('malformed-response', status, url, false);
}
